import { useRouter } from 'next/router'
import React, { FC, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import * as PT from 'modules/Pricing/types'
import { getFeaturesDifference, getFeaturesIncluded, getPackageByName } from 'modules/Pricing/utils'

import ErrorBox from 'components/ErrorBox'
import LoadingBox from 'components/LoadingBox'

import { useAgreementSigningState, useWindowSize } from 'services/hooks'
import { addPackageToCart } from 'services/order'
import { useCart } from 'services/swr/orders/useCart'
import { useListingPricingPackageOrder } from 'services/swr/orders/useListingPricingPackageOrder'
import { useListing } from 'services/swr/useListing'

import * as T from 'types'
import * as E from 'types/enums'

import PackageCards from './PackageCards'
import PackageCardsGrid from './PackageCardsGrid'

const PackagesContainer = styled.div``

interface IProps {
  selectable?: boolean
  pricingPackages: T.IPricingPackage[]
  items: T.IPricingPackageItem[]
  selectedState: T.IState | null
}

const PricingPackages: FC<IProps> = ({ selectable, pricingPackages, items, selectedState }) => {
  // router
  const router = useRouter()
  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()
  const isDesktop = width && width >= theme.metrics.desktop

  // swr
  const { cart, error: cartError, mutate } = useCart()
  const { listing, error: listingError } = useListing()
  const { listingPricingPackage } = useListingPricingPackageOrder(listing)

  // agreement
  const { agreement } = useAgreementSigningState(listing)

  // don't allow a user to select anything until after we're fully initialized
  const [initializedPurchased, setInitializedPurchased] = useState(false)
  const [initializedInCart, setInitializedInCart] = useState(false)
  const initialized = initializedPurchased && initializedInCart
  useEffect(() => {
    // make sure we rerun the effects below whenever the user comes back to this page
    setInitializedInCart(false)
    setInitializedPurchased(false)
  }, [router.pathname])

  // purchased package
  const [purchasedPackage, setPurchasedPackage] = useState<E.PackageType | undefined>()
  useEffect(() => {
    if (!listing) return

    if (!listingPricingPackage) setPurchasedPackage(undefined)

    const listingPackage = listing.pricingPackage?.name as E.PackageType

    // premium and platinum we must confirm there was payment
    const isPaid = listingPricingPackage?.paid

    // basic + agreement signed is equivalent to having "paid", for the purposes of this component
    const basicPaid = listingPackage === E.PackageType.basic && !!agreement
    if (basicPaid || isPaid) setPurchasedPackage(listingPackage)

    setInitializedPurchased(true)
  }, [listing, agreement, listingPricingPackage])

  // package in cart
  const [packageInCart, setPackageInCart] = useState(E.PackageType.basic)
  const [currentServiceId, setCurrentServiceId] = useState('')
  useEffect(() => {
    if (!listing || !cart || initializedInCart) return

    if (purchasedPackage) return setInitializedInCart(true)

    // set package in the cart if it exists in the cart order object
    const listingServices = cart?.items.find(itm => itm.listing._id === listing?._id)?.services
    const pricingPackage = listingServices?.find(srv => srv.type.status === 'PricingPackage')

    if (!!pricingPackage && pricingPackage.type.name !== packageInCart) {
      setPackageInCart(pricingPackage.type.name as E.PackageType)
    } else if (!pricingPackage) {
      setPackageInCart(E.PackageType.basic)
    }

    setInitializedInCart(true)
  }, [cart, listing])

  const cartListError = "We can't add a package to your cart at this time. Please contact support"
  if (selectable && (listingError || cartError)) return <ErrorBox error={cartListError} />

  if (!pricingPackages || !items) return <LoadingBox height={800} />

  if (selectable && (!listing || !cart)) return <LoadingBox height={800} />

  // lots of data munging for proper UI display below
  const basicFeatures = getFeaturesIncluded(E.PackageType.basic, pricingPackages, items)
  const premiumFeatures = getFeaturesIncluded(E.PackageType.premium, pricingPackages, items)
  const platinumFeatures = getFeaturesIncluded(E.PackageType.platinum, pricingPackages, items)
  const eliteFeatures = getFeaturesIncluded(E.PackageType.elite, pricingPackages, items)

  const featuresExclusive: PT.IFeatures = {
    Basic: basicFeatures,
    Premium: getFeaturesDifference(basicFeatures, premiumFeatures),
    Platinum: getFeaturesDifference(premiumFeatures, platinumFeatures),
    Elite: getFeaturesDifference(eliteFeatures, eliteFeatures),
  }

  const pricingPackagesObj: PT.IPricingPackageObject = {
    Basic: getPackageByName(E.PackageType.basic, pricingPackages),
    Premium: getPackageByName(E.PackageType.premium, pricingPackages),
    Platinum: getPackageByName(E.PackageType.platinum, pricingPackages),
    Elite: getPackageByName(E.PackageType.elite, pricingPackages),
  }

  // click handlers
  const handleSelect = async (packageType: E.PackageType) => {
    if (!cart || cartError || !listing || !pricingPackages || !!purchasedPackage) return

    // don't re-select already selected packages
    const pricingPack = getPackageByName(packageType, pricingPackages)
    if (currentServiceId === pricingPack?.service?._id) return

    const isServiceMatch = (srv: T.IOrderService) => String(srv.type) === pricingPack?.service?._id
    if (cart.items.some(item => item.services.some(isServiceMatch))) return

    // do select different packages and add them to the cart
    setPackageInCart(packageType)
    if (pricingPack?.service?._id) setCurrentServiceId(pricingPack.service._id)

    const updatedCart = await addPackageToCart(cart, pricingPack, listing._id)
    mutate(updatedCart)
  }

  return (
    <PackagesContainer>
      {!isDesktop
        ? (
          <PackageCards
            pricingPackagesObj={pricingPackagesObj}
            featuresExclusive={featuresExclusive}
            packageInCart={packageInCart}
            onClickSelect={handleSelect}
            purchasedPackage={purchasedPackage}
            listingPricingPackage={listingPricingPackage}
            selectable={selectable}
            initialized={initialized}
          />
        )
        : (
          <PackageCardsGrid
            pricingPackagesObj={pricingPackagesObj}
            featuresExclusive={featuresExclusive}
            packageInCart={packageInCart}
            onClickSelect={handleSelect}
            purchasedPackage={purchasedPackage}
            listingPricingPackage={listingPricingPackage}
            selectable={selectable}
            initialized={initialized}
            selectedState={selectedState}
          />
        )}
    </PackagesContainer>
  )
}

export default PricingPackages
